import React, {useCallback, useMemo} from 'react';

import {Box, Collapse, Radio, Typography} from '@mui/material';

import './styles';
import {COLORS} from '@/styles/colors';

import {PaymentMethodOption, PaymentMethodV2Type} from './models/types';

type PaymentMethodsSelectorProps = {
  options: ReadonlyArray<PaymentMethodOption>;
  selectedId: PaymentMethodV2Type | null;
  onSelect: (id: PaymentMethodV2Type) => void;
  renderForm: (selectedId: PaymentMethodV2Type) => React.ReactNode;
  children?: React.ReactNode;
};

const PaymentMethodsSelector: React.FC<PaymentMethodsSelectorProps> = ({
  options,
  selectedId,
  onSelect,
  renderForm,
  children,
}) => {
  const visibleOptions = useMemo(
    () => options?.filter(opt => opt.visible),
    [options],
  );

  const getOptionStyle = useCallback(
    (
      isFirst: boolean,
      isLast: boolean,
      selected: boolean,
    ): React.CSSProperties => ({
      borderBottom:
        !isLast && !selected ? `1px solid ${COLORS.NEUTRAL_300}` : 'none',
      borderTop:
        !isFirst && !selected ? `1px solid ${COLORS.NEUTRAL_300}` : 'none',
    }),
    [],
  );

  return (
    <Box
      role="radiogroup"
      display="flex"
      flexDirection="column">
      <div className="payment-method-selector-container">
        {visibleOptions.map((opt, idx) => {
          const selected = selectedId === opt.id;
          const isFirst = idx === 0;
          const isLast = idx === visibleOptions.length - 1;

          const style = getOptionStyle(isFirst, isLast, selected);

          return (
            <div key={opt.id}>
              <div
                role="radio"
                aria-checked={selected}
                onClick={() => !opt.disabled && onSelect(opt.id)}
                style={style}
                className="flex p-24 gap-14">
                <Radio
                  checked={selected}
                  disabled={opt.disabled}
                  value={opt.id}
                  onChange={() => onSelect(opt.id)}
                  sx={{
                    p: 0.5,
                    color: COLORS.ON_SURFACE_1,
                    '&.Mui-checked': {color: COLORS.ON_SURFACE_1},
                  }}
                />

                <Box
                  display="flex"
                  alignItems="center"
                  gap={2}>
                  <Box
                    component="img"
                    src={
                      selected && opt.selectedIconSrc
                        ? opt.selectedIconSrc
                        : opt.iconSrc
                    }
                    alt="payment method"
                    sx={{width: 37, height: 24}}
                  />
                  <Typography
                    fontWeight={400}
                    sx={{
                      color: COLORS.ON_SURFACE,
                    }}>
                    {opt.label}
                  </Typography>
                </Box>
              </div>

              <Collapse
                in={selected}
                timeout="auto"
                unmountOnExit>
                <Box>{renderForm(opt.id)}</Box>
                {selected && children}
              </Collapse>
            </div>
          );
        })}
      </div>
    </Box>
  );
};

export default PaymentMethodsSelector;
